import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Clock } from 'lucide-react';
import { useAuthStore } from '../store/auth.store';
import { authApi } from '../services/api';
import { disconnectSocket } from '../services/socket';

const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const WARNING_LEAD_MS = 60 * 1000;
const TICK_MS = 5_000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;

const API_URL = import.meta.env.VITE_API_URL || '';

export default function InactivityGuard() {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setTokens = useAuthStore((s) => s.setTokens);
  const logoutStore = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARNING_LEAD_MS / 1000));

  const lastActivityRef = useRef<number>(Date.now());
  const warningOpenRef = useRef<boolean>(false);
  const loggingOutRef = useRef<boolean>(false);

  const performLogout = useCallback(async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    try {
      await authApi.logout();
    } catch {
      // ignore — we want to log out client-side regardless
    }
    disconnectSocket();
    logoutStore();
    navigate('/login', { replace: true });
  }, [logoutStore, navigate]);

  const stayConnected = useCallback(async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/refresh`,
        {},
        { withCredentials: true },
      );
      const { accessToken } = response.data;
      if (accessToken) setTokens(accessToken);
      lastActivityRef.current = Date.now();
      warningOpenRef.current = false;
      setWarningOpen(false);
    } catch {
      await performLogout();
    }
  }, [performLogout, setTokens]);

  useEffect(() => {
    if (!isAuthenticated) {
      warningOpenRef.current = false;
      setWarningOpen(false);
      return;
    }

    lastActivityRef.current = Date.now();

    const onActivity = () => {
      if (warningOpenRef.current) return;
      lastActivityRef.current = Date.now();
    };

    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }

    const interval = window.setInterval(() => {
      const idleFor = Date.now() - lastActivityRef.current;

      if (idleFor >= IDLE_TIMEOUT_MS) {
        performLogout();
        return;
      }

      const msUntilLogout = IDLE_TIMEOUT_MS - idleFor;
      if (msUntilLogout <= WARNING_LEAD_MS) {
        if (!warningOpenRef.current) {
          warningOpenRef.current = true;
          setWarningOpen(true);
        }
        setSecondsLeft(Math.max(0, Math.ceil(msUntilLogout / 1000)));
      }
    }, TICK_MS);

    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, onActivity);
      }
      window.clearInterval(interval);
    };
  }, [isAuthenticated, performLogout]);

  if (!isAuthenticated || !warningOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-dark-900 border border-dark-700 rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 pt-8 pb-6 bg-gradient-to-br from-amber-500 to-orange-600 text-white text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-2xl flex items-center justify-center">
            <Clock className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold">{t('inactivity_guard.title')}</h2>
          <p className="mt-1 text-amber-100 text-sm">{t('inactivity_guard.subtitle')}</p>
        </div>

        <div className="p-6 text-center">
          <p className="text-dark-200">
            {t('inactivity_guard.countdown_before')}{' '}
            <span className="font-bold text-amber-400 text-lg">{t('inactivity_guard.countdown_seconds', { seconds: secondsLeft })}</span>
          </p>
          <p className="mt-2 text-sm text-dark-400">
            {t('inactivity_guard.explainer')}
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              onClick={performLogout}
              className="flex-1 px-4 py-2.5 rounded-lg border border-dark-700 text-dark-200 hover:bg-dark-800 transition-colors"
            >
              {t('inactivity_guard.logout')}
            </button>
            <button
              onClick={stayConnected}
              className="flex-1 px-4 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white font-medium transition-colors"
            >
              {t('inactivity_guard.stay')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
